import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';

const debounce = (func, delay) => {
    let timer;
    return function (...args) {
        clearTimeout(timer);
        timer = setTimeout(() => {
            func.apply(this, args);
        }, delay);
    };
};


const PopupContainer = ({ ctrlRef, children }) => {
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const divRef = useRef();

    const updatePosition = useCallback(() => {
        if (!ctrlRef.current || !divRef.current) {
            return;
        }
        const rect = ctrlRef.current.getBoundingClientRect();
        const div = divRef.current.getBoundingClientRect();
        setPosition({
            top: rect.top + window.scrollY + rect.height,
            left: rect.left + window.scrollX + rect.width - div.width
        });
    }, [ctrlRef]);

    useEffect(() => {
        // Set up an observer to watch for changes in the buttonRef
        const observer = new MutationObserver(() => {
            if (ctrlRef.current) {
                updatePosition();
            }
        });

        if (ctrlRef.current) {
            updatePosition();
        } else {
            observer.observe(document, { childList: true, subtree: true });
        }

        const debouncedUpdatePosition = debounce(updatePosition, 100);

        window.addEventListener('resize', debouncedUpdatePosition);
        window.addEventListener('scroll', debouncedUpdatePosition, true);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', debouncedUpdatePosition);
            window.removeEventListener('scroll', debouncedUpdatePosition);
        };
    }, [ctrlRef, updatePosition]);

    return createPortal(<div ref={divRef} className="p-2" style={{ position: 'absolute', top: position.top + 'px', left: position.left + 'px', backgroundColor: 'white', border: '1px solid grey', borderRadius: '4px', zIndex: 10000 }}>
        {children}
    </div>, document.body)
}

PopupContainer.propTypes = {
    /**
     * Reference to the control that will trigger the popup and which we should use for positioning
     */
    ctrlRef: PropTypes.any.isRequired,

    /**
     * The controls to render in the popup
     */
    children: PropTypes.any
}

export default PopupContainer;